/**
 * RBAC constants for Medplum-backed role and permission resolution.
 *
 * Extension URLs and policy IDs are sourced from medplum-server `fhir-model`
 * (single source of truth). UI-specific names are preserved for callers.
 */

import {
  ATTENDANCE_STATUS_EXT,
  EXT_URLS,
  IOP_PARENT_ENCOUNTER_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { POLICY_IDS as FHIR_POLICY_IDS } from '@unify/fhir-model/structural-ids';

// ---------------------------------------------------------------------------
// FHIR extension URLs (aligned with medplum-server/src/fhir-model/constants)
// ---------------------------------------------------------------------------

export const EXTENSION_URLS = {
  /** Role slug stored on Practitioner/Patient/RelatedPerson profile. */
  role: EXT_URLS.role,
  /** Resolved UI-permission JSON stored on the profile by a Bot/seed. */
  uiPermissions: EXT_URLS.uiPermissions,
  /** Raw per-user permission overrides (sparse object, merged over role defaults). */
  uiPermissionOverrides: EXT_URLS.uiPermissionOverrides,
  /** Guarantor/proxy login: primary Patient whose portal data to load (Patient reference). */
  portalSubjectPatient: EXT_URLS.portalSubjectPatient,
  /** Client+guarantor: additional Patient(s) this user can view in the portal (repeatable). */
  portalRelatedPatient: EXT_URLS.portalRelatedPatient,
  /** IOP: child Encounter → parent Encounter reference for daily rollup billing. */
  iopParentEncounter: IOP_PARENT_ENCOUNTER_EXT,
  /** Attendance status on Appointment.participant (extended beyond FHIR base). */
  attendanceStatus: ATTENDANCE_STATUS_EXT,
  /** Therapy service type CodeSystem URI (coding.system on Appointment/Encounter). */
  therapyServiceType: THERAPY_SERVICE_TYPE_SYSTEM,
} as const;

// ---------------------------------------------------------------------------
// AccessPolicy resource IDs (pilot seed — must match structural-ids.ts)
// ---------------------------------------------------------------------------

export const POLICY_IDS = {
  /** Patient can see only their own data. */
  patientSelfService: FHIR_POLICY_IDS.patientSelf,
  /** Provider: CareTeam compartment + org. */
  providerScoped: FHIR_POLICY_IDS.provider,
  /** Supervisor: read-heavy CareTeam scoped policy. */
  supervisorScoped: FHIR_POLICY_IDS.supervisor,
  /** Guarantor with billing-only portal. */
  guarantorBillingProxy: FHIR_POLICY_IDS.guarantorBilling,
  /** Guarantor with clinical proxy. */
  guarantorClinicalProxy: FHIR_POLICY_IDS.guarantorClinical,
  /** Client + guarantor: self-service plus access to a related patient's data. */
  clientGuarantor: FHIR_POLICY_IDS.clientGuarantor,
  /** Episode-scoped clinical template (parameterized: episode, patient, related_person). */
  episodeClinical: FHIR_POLICY_IDS.episodeClinical,
  /** Episode-scoped billing template (parameterized: episode, patient, account, related_person). */
  episodeBilling: FHIR_POLICY_IDS.episodeBilling,
  /**
   * Unify Admin: full access to all resource types in the project, no criteria restrictions.
   */
  unifyAdmin: FHIR_POLICY_IDS.unifyAdmin,
  /** Unify Assistant: same as admin except Organization is read/search of practices and payers only. */
  unifyAssistant: FHIR_POLICY_IDS.unifyAssistant,
  /** Intake-lead-patient Bot — pilot-tagged Patient create for intake (staff / client portal). */
  intakeLeadBot: FHIR_POLICY_IDS.intakeLeadBot,
  /** OAuth ClientApplication (`client_credentials`) for intake without an interactive user session. */
  publicIntakeClient: FHIR_POLICY_IDS.publicIntakeClient,
  /**
   * Practice Owner / Practice Admin / Practice Assistant: access scoped to their Organization.
   */
  practiceAdmin: FHIR_POLICY_IDS.practiceAdmin,
  practiceAssistant: FHIR_POLICY_IDS.practiceAssistant,
} as const;

// ---------------------------------------------------------------------------
// Profile resource type helpers
// ---------------------------------------------------------------------------

export type MedplumProfileType = 'Patient' | 'Practitioner' | 'RelatedPerson';

export function isMedplumProfileType(rt: string | undefined): rt is MedplumProfileType {
  return rt === 'Patient' || rt === 'Practitioner' || rt === 'RelatedPerson';
}
